/**
 * Code-review services — the four mutations a researcher can run while a
 * Grounded Emergence analysis is in `paused_for_review`. These wrap the
 * existing customer-api endpoints with thin, well-typed call functions
 * (no shared state with the older interview services so cache concerns
 * stay localised to the code-review hooks).
 *
 * Endpoint targets (customer-api / Mongo behaviour):
 *   renameCode        PATCH /qualitative-studies/{studyId}/codes/edit
 *                     — renames a code everywhere it's applied across the
 *                       analysis_item's codes_quotes_dict. URL path param
 *                       is documented as `transcript_id` upstream but the
 *                       handler treats it as study_id (no turn scope).
 *   deleteCode        PATCH /qualitative-studies/{studyId}/codes/delete
 *                     — removes the code entirely from the analysis_item.
 *   unassignCode...   PATCH /qualitative-studies/{itemId}/codes/remove
 *                     — drops the code from one turn / paragraph only.
 *   createCodeOnTurn  PATCH /qualitative-studies/{itemId}/codes/create
 *                     — adds a NEW code with a user-supplied definition
 *                       and attaches it to one turn. The first turn-attach
 *                       creates the code in the codebook; subsequent
 *                       attachments just append to its `sources` list.
 */
import axiosServices from '@utils/axios';

const backendUrl = import.meta.env.VITE_BACKEND_URL;

export async function renameCode({
  studyId,
  oldCode,
  newCode,
  definition = null,
  analysisItemId = null,
}) {
  const url = `${backendUrl}/qualitative-studies/${studyId}/codes/edit`;
  const { data } = await axiosServices.patch(url, {
    study_id: studyId,
    old_code: oldCode,
    new_code: newCode,
    definition,
    ...(analysisItemId ? { analysis_item_id: analysisItemId } : {}),
  });
  return data;
}

export async function deleteCode({ studyId, code, analysisItemId = null }) {
  const url = `${backendUrl}/qualitative-studies/${studyId}/codes/delete`;
  const { data } = await axiosServices.patch(url, {
    study_id: studyId,
    code,
    ...(analysisItemId ? { analysis_item_id: analysisItemId } : {}),
  });
  return data;
}

export async function unassignCodeFromTurn({
  studyId,
  qualitativeItemId,
  code,
  turnId,
  analysisItemId = null,
}) {
  const url = `${backendUrl}/qualitative-studies/${qualitativeItemId}/codes/remove`;
  const { data } = await axiosServices.patch(url, {
    study_id: studyId,
    qualitative_item_id: qualitativeItemId,
    code,
    turn_id: turnId,
    ...(analysisItemId ? { analysis_item_id: analysisItemId } : {}),
  });
  return data;
}

export async function createCodeOnTurn({
  studyId,
  qualitativeItemId,
  codeName,
  turnId,
  definition = '',
  highlightedText = '',
  analysisItemId = null,
}) {
  const url = `${backendUrl}/qualitative-studies/${qualitativeItemId}/codes/create`;
  const { data } = await axiosServices.patch(url, {
    study_id: studyId,
    qualitative_item_id: qualitativeItemId,
    // OpenCode shape on the server: { name, location?, sentiment? }.
    // Location + sentiment aren't meaningful for a researcher-authored
    // assignment, so we send only the name; the server defaults the rest.
    code: { name: codeName },
    turn_id: turnId,
    definition,
    highlighted_text: highlightedText,
    ...(analysisItemId ? { analysis_item_id: analysisItemId } : {}),
  });
  return data;
}

export async function assignCodeToTurn({
  studyId,
  qualitativeItemId,
  codeName,
  turnId,
  highlightedText = '',
  analysisItemId = null,
}) {
  // Attach an ALREADY-EXISTING code (one already in codes_quotes_dict)
  // to a turn. The server accepts a list of codes per call but we keep
  // the UI one-at-a-time for simpler optimistic feedback. Definition is
  // omitted — the server preserves whatever definition the code already
  // carries for AI-generated codes; user-created codes are routed via
  // createCodeOnTurn instead and carry their own definition.
  const url = `${backendUrl}/qualitative-studies/${qualitativeItemId}/codes/assign_codes`;
  const { data } = await axiosServices.patch(url, {
    study_id: studyId,
    qualitative_item_id: qualitativeItemId,
    codes: [{ code: { name: codeName } }],
    turn_id: turnId,
    highlighted_text: highlightedText,
    ...(analysisItemId ? { analysis_item_id: analysisItemId } : {}),
  });
  return data;
}
